import { useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { Select, Switch, InputNumber, Input } from "antd";

const FIELD_OPTIONS = [
  { label: "Name", value: "name" },
  { label: "Phone", value: "phone" },
  { label: "Email", value: "email" },
  { label: "Guests", value: "guests" },
  { label: "Date", value: "date" },
  { label: "Time", value: "time" },
  { label: "Special Request", value: "special_requests" }
];

export default function BookingFormSettingsSection({ config, onChange }) {
  const bookingForm = config?.bookingForm || {};

  const requiredFields = bookingForm.requiredFields || ["name", "phone", "guests"];
  const showTimePicker = bookingForm.showTimePicker ?? true;
  const minGuests = Number.isFinite(bookingForm.minGuests) ? bookingForm.minGuests : 1;
  const maxGuests = Number.isFinite(bookingForm.maxGuests) ? bookingForm.maxGuests : 10;
  const allowSpecialRequests = bookingForm.allowSpecialRequests ?? true;
  const allowInternalNotes = bookingForm.allowInternalNotes ?? true;
  const redirectAfterSubmissionUrl = bookingForm.redirectAfterSubmissionUrl || "";

  const update = (patch) => {
    const next = structuredClone(config);
    next.bookingForm = next.bookingForm || {};
    Object.assign(next.bookingForm, patch);
    onChange(next);
  };

  const safeRequiredFields = useMemo(() => {
    // ensure required fields are unique and valid
    const valid = new Set(FIELD_OPTIONS.map((o) => o.value));
    return Array.from(new Set(requiredFields)).filter((v) => valid.has(v));
  }, [requiredFields]);

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h5 className="mb-1">Reservation Settings</h5>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Control what guests must fill, guest limits, time picker and post-submit behavior.
            </div>
          </div>
        </div>

        <hr className="my-3" />

        <Row className="g-3">
          <Col md={12}>
            <label className="form-label">Required Fields</label>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              value={safeRequiredFields}
              onChange={(vals) => update({ requiredFields: vals })}
              options={FIELD_OPTIONS}
              placeholder="Select required fields"
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              These fields will be enforced before reservation can be submitted.
            </div>
          </Col>

          <Col md={6}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div style={{ fontWeight: 700 }}>Show Time Picker</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  If off, system can auto-suggest slots based on rules.
                </div>
              </div>
              <Switch checked={showTimePicker} onChange={(v) => update({ showTimePicker: v })} />
            </div>
          </Col>

          <Col md={6}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div style={{ fontWeight: 700 }}>Allow Special Requests</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Show “Special requests” text area to guests.
                </div>
              </div>
              <Switch
                checked={allowSpecialRequests}
                onChange={(v) => update({ allowSpecialRequests: v })}
              />
            </div>
          </Col>

          <Col md={6}>
            <label className="form-label">Guest Count Control</label>
            <div className="d-flex gap-2">
              <InputNumber
                min={1}
                max={999}
                value={minGuests}
                style={{ width: "100%" }}
                onChange={(v) => update({ minGuests: Number(v || 1) })}
                placeholder="Min"
              />
              <InputNumber
                min={1}
                max={999}
                value={maxGuests}
                style={{ width: "100%" }}
                onChange={(v) => update({ maxGuests: Number(v || 10) })}
                placeholder="Max"
              />
            </div>
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Used across widget + dedicated booking page validations.
            </div>
          </Col>

          <Col md={6}>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div style={{ fontWeight: 700 }}>Allow Internal Notes</div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Staff-only notes in admin dashboard (not visible to guests).
                </div>
              </div>
              <Switch
                checked={allowInternalNotes}
                onChange={(v) => update({ allowInternalNotes: v })}
              />
            </div>
          </Col>

          <Col md={12}>
            <label className="form-label">Redirect After Submission</label>
            <Input
              value={redirectAfterSubmissionUrl}
              onChange={(e) => update({ redirectAfterSubmissionUrl: e.target.value })}
              placeholder="Thank you page URL"
            />
            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
              Optional. If provided, guests are redirected after successful reservation.
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}
